import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { markdownToHtml } from "@/lib/markdown";
import { printMaterialToPDF } from "@/lib/pdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Copy, Save, Download, Wand2, ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { DISCIPLINAS, SERIES, NIVEIS, DIFICULDADES, TIPOS_QUESTAO, ACOES_ADAPTAR } from "@/lib/options";

/* Field primitives ------------------------------------------------------- */
function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-semibold text-slate-700">{label}</Label>
      {children}
    </div>
  );
}
function SelectField({ value, onChange, options, placeholder = "Selecione", testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="rounded-xl h-11" data-testid={testid}><SelectValue placeholder={placeholder}/></SelectTrigger>
      <SelectContent>{options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}

/* Common result panel ---------------------------------------------------- */
function ResultPanel({ conteudo, setConteudo, meta, onRegenerate, tipo }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [titulo, setTitulo] = useState(meta.tema || "Novo material");

  useEffect(() => { setTitulo(meta.tema || "Novo material"); }, [meta.tema]);

  const copy = async () => {
    await navigator.clipboard.writeText(conteudo);
    toast.success("Conteúdo copiado!");
  };

  const save = async () => {
    try {
      const { data } = await api.post("/materiais", {
        tipo, titulo, disciplina: meta.disciplina, serie: meta.serie, tema: meta.tema, conteudo,
      });
      toast.success("Material salvo em Meus Materiais.");
      nav(`/materiais/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const download = () => {
    printMaterialToPDF({ titulo, disciplina: meta.disciplina, serie: meta.serie, tema: meta.tema, conteudo }, user?.name);
  };

  const html = markdownToHtml(conteudo);

  const goCreate = (path) => {
    nav(path, { state: { fromMaterial: { disciplina: meta.disciplina, serie: meta.serie, tema: meta.tema, conteudo } } });
  };

  return (
    <div className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6" data-testid="result-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex-1">
          <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">Título</Label>
          <Input value={titulo} onChange={e => setTitulo(e.target.value)} className="mt-1 font-heading text-lg font-bold" data-testid="material-title-input"/>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">Editar (Markdown)</Label>
          <Textarea value={conteudo} onChange={e => setConteudo(e.target.value)}
            className="min-h-[520px] font-mono text-sm rounded-xl" data-testid="content-editor"/>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">Prévia</Label>
          <div className="min-h-[520px] max-h-[520px] overflow-y-auto rounded-xl border border-slate-200 p-5 bg-white prose-ap" dangerouslySetInnerHTML={{ __html: html }}/>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <Button onClick={onRegenerate} variant="outline" className="rounded-full border-2 border-[#1F3A8A] text-[#1F3A8A]" data-testid="btn-regenerate">
          <RefreshCw className="mr-2" size={16}/> Regenerar
        </Button>
        <Button onClick={copy} variant="outline" className="rounded-full" data-testid="btn-copy">
          <Copy className="mr-2" size={16}/> Copiar
        </Button>
        <Button onClick={save} className="rounded-full bg-[#8B5CF6] hover:bg-[#7c4ef4]" data-testid="btn-save">
          <Save className="mr-2" size={16}/> Salvar
        </Button>
        <Button onClick={download} className="rounded-full bg-[#1F3A8A] hover:bg-[#15265b]" data-testid="btn-download-pdf">
          <Download className="mr-2" size={16}/> Baixar PDF
        </Button>
        {tipo === "plano" && (
          <>
            <Button variant="ghost" className="rounded-full" onClick={() => goCreate("/atividade")} data-testid="btn-create-atividade">
              Criar atividade com este conteúdo
            </Button>
            <Button variant="ghost" className="rounded-full" onClick={() => goCreate("/avaliacao")} data-testid="btn-create-avaliacao">
              Criar avaliação com este conteúdo
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/* Page wrapper ----------------------------------------------------------- */
function ToolShell({ title, subtitle, children }) {
  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-dashboard">
        <ArrowLeft size={14}/> Voltar
      </Link>
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">{title}</h1>
      <p className="mt-2 text-slate-600">{subtitle}</p>
      <div className="mt-8">{children}</div>
    </div>
  );
}

/* Plano de Aula ---------------------------------------------------------- */
export function PlanoAulaPage() {
  const location = useLocation();
  const seed = location.state?.fromMaterial;
  const [form, setForm] = useState({
    disciplina: seed?.disciplina || "Matemática",
    serie: seed?.serie || "6º ano",
    tema: seed?.tema || "",
    duracao: "50 minutos",
    nivel: "Regular",
    objetivo: "",
    observacoes: "",
  });
  const [conteudo, setConteudo] = useState("");
  const [loading, setLoading] = useState(false);
  const { refreshUsage } = useAuth();

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/ai/plano-aula", form);
      setConteudo(data.conteudo);
      refreshUsage();
      toast.success("Plano gerado!");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <ToolShell title="Plano de Aula" subtitle="Preencha os dados. A IA cria a estrutura completa.">
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 bg-white rounded-2xl border border-slate-200 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <Field label="Disciplina"><SelectField value={form.disciplina} onChange={v => setForm({...form, disciplina: v})} options={DISCIPLINAS} testid="select-disciplina"/></Field>
        <Field label="Ano/Série"><SelectField value={form.serie} onChange={v => setForm({...form, serie: v})} options={SERIES} testid="select-serie"/></Field>
        <Field label="Tema/Assunto"><Input required value={form.tema} onChange={e => setForm({...form, tema: e.target.value})} className="rounded-xl h-11" placeholder="Ex.: Frações equivalentes" data-testid="input-tema"/></Field>
        <Field label="Duração da aula"><Input value={form.duracao} onChange={e => setForm({...form, duracao: e.target.value})} className="rounded-xl h-11" placeholder="Ex.: 50 minutos" data-testid="input-duracao"/></Field>
        <Field label="Nível da turma"><SelectField value={form.nivel} onChange={v => setForm({...form, nivel: v})} options={NIVEIS} testid="select-nivel"/></Field>
        <Field label="Objetivo da aula"><Input value={form.objetivo} onChange={e => setForm({...form, objetivo: e.target.value})} className="rounded-xl h-11" placeholder="Ex.: Reconhecer frações equivalentes" data-testid="input-objetivo"/></Field>
        <div className="sm:col-span-2">
          <Field label="Observações adicionais"><Textarea value={form.observacoes} onChange={e => setForm({...form, observacoes: e.target.value})} className="rounded-xl" placeholder="Recursos disponíveis, características da turma..." data-testid="input-observacoes"/></Field>
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <Button disabled={loading} className="rounded-full bg-[#1F3A8A] hover:bg-[#15265b] px-8 h-12 text-base font-semibold" data-testid="btn-generate">
            {loading ? <Loader2 className="animate-spin mr-2" size={18}/> : <Wand2 className="mr-2" size={18}/>}
            Gerar Plano de Aula
          </Button>
        </div>
      </form>

      {conteudo && <ResultPanel conteudo={conteudo} setConteudo={setConteudo}
        meta={{ disciplina: form.disciplina, serie: form.serie, tema: form.tema }}
        onRegenerate={() => submit()} tipo="plano"/>}
    </ToolShell>
  );
}

/* Atividade -------------------------------------------------------------- */
export function AtividadePage() {
  const location = useLocation();
  const seed = location.state?.fromMaterial;
  const [form, setForm] = useState({
    disciplina: seed?.disciplina || "Matemática",
    serie: seed?.serie || "6º ano",
    tema: seed?.tema || "",
    quantidade: 10,
    dificuldade: "Média",
    tipo_questao: "Mista",
    observacoes: seed?.conteudo ? "Base de conteúdo:\n" + seed.conteudo.slice(0, 1500) : "",
  });
  const [conteudo, setConteudo] = useState("");
  const [loading, setLoading] = useState(false);
  const { refreshUsage } = useAuth();

  const submit = async (e) => {
    e?.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/ai/atividade", form);
      setConteudo(data.conteudo); refreshUsage(); toast.success("Atividade gerada!");
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  return (
    <ToolShell title="Atividade" subtitle="Gere listas de exercícios com gabarito automático.">
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 bg-white rounded-2xl border border-slate-200 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <Field label="Disciplina"><SelectField value={form.disciplina} onChange={v => setForm({...form, disciplina: v})} options={DISCIPLINAS} testid="select-disciplina"/></Field>
        <Field label="Ano/Série"><SelectField value={form.serie} onChange={v => setForm({...form, serie: v})} options={SERIES} testid="select-serie"/></Field>
        <Field label="Tema"><Input required value={form.tema} onChange={e => setForm({...form, tema: e.target.value})} className="rounded-xl h-11" data-testid="input-tema"/></Field>
        <Field label="Quantidade de questões"><Input type="number" min={1} max={30} value={form.quantidade} onChange={e => setForm({...form, quantidade: parseInt(e.target.value)||1})} className="rounded-xl h-11" data-testid="input-quantidade"/></Field>
        <Field label="Nível de dificuldade"><SelectField value={form.dificuldade} onChange={v => setForm({...form, dificuldade: v})} options={DIFICULDADES} testid="select-dificuldade"/></Field>
        <Field label="Tipo de questão"><SelectField value={form.tipo_questao} onChange={v => setForm({...form, tipo_questao: v})} options={TIPOS_QUESTAO} testid="select-tipo"/></Field>
        <div className="sm:col-span-2">
          <Field label="Observações"><Textarea value={form.observacoes} onChange={e => setForm({...form, observacoes: e.target.value})} className="rounded-xl" data-testid="input-observacoes"/></Field>
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <Button disabled={loading} className="rounded-full bg-[#1F3A8A] hover:bg-[#15265b] px-8 h-12 text-base font-semibold" data-testid="btn-generate">
            {loading ? <Loader2 className="animate-spin mr-2" size={18}/> : <Wand2 className="mr-2" size={18}/>} Gerar Atividade
          </Button>
        </div>
      </form>
      {conteudo && <ResultPanel conteudo={conteudo} setConteudo={setConteudo}
        meta={{ disciplina: form.disciplina, serie: form.serie, tema: form.tema }}
        onRegenerate={() => submit()} tipo="atividade"/>}
    </ToolShell>
  );
}

/* Avaliação -------------------------------------------------------------- */
export function AvaliacaoPage() {
  const location = useLocation();
  const seed = location.state?.fromMaterial;
  const [form, setForm] = useState({
    disciplina: seed?.disciplina || "Matemática",
    serie: seed?.serie || "6º ano",
    tema: seed?.tema || "",
    quantidade: 10,
    dificuldade: "Média",
    tipo_questao: "Mista",
    valor: "10,0",
  });
  const [conteudo, setConteudo] = useState("");
  const [loading, setLoading] = useState(false);
  const { refreshUsage } = useAuth();

  const submit = async (e) => {
    e?.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/ai/avaliacao", form);
      setConteudo(data.conteudo); refreshUsage(); toast.success("Avaliação gerada!");
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  return (
    <ToolShell title="Avaliação" subtitle="Prova pronta para impressão, com gabarito para o professor.">
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 bg-white rounded-2xl border border-slate-200 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <Field label="Disciplina"><SelectField value={form.disciplina} onChange={v => setForm({...form, disciplina: v})} options={DISCIPLINAS} testid="select-disciplina"/></Field>
        <Field label="Ano/Série"><SelectField value={form.serie} onChange={v => setForm({...form, serie: v})} options={SERIES} testid="select-serie"/></Field>
        <Field label="Tema/Conteúdo"><Input required value={form.tema} onChange={e => setForm({...form, tema: e.target.value})} className="rounded-xl h-11" data-testid="input-tema"/></Field>
        <Field label="Número de questões"><Input type="number" min={1} max={30} value={form.quantidade} onChange={e => setForm({...form, quantidade: parseInt(e.target.value)||1})} className="rounded-xl h-11" data-testid="input-quantidade"/></Field>
        <Field label="Nível de dificuldade"><SelectField value={form.dificuldade} onChange={v => setForm({...form, dificuldade: v})} options={DIFICULDADES} testid="select-dificuldade"/></Field>
        <Field label="Tipo de questão"><SelectField value={form.tipo_questao} onChange={v => setForm({...form, tipo_questao: v})} options={TIPOS_QUESTAO} testid="select-tipo"/></Field>
        <Field label="Valor total"><Input value={form.valor} onChange={e => setForm({...form, valor: e.target.value})} className="rounded-xl h-11" data-testid="input-valor"/></Field>
        <div className="sm:col-span-2 flex justify-end">
          <Button disabled={loading} className="rounded-full bg-[#1F3A8A] hover:bg-[#15265b] px-8 h-12 text-base font-semibold" data-testid="btn-generate">
            {loading ? <Loader2 className="animate-spin mr-2" size={18}/> : <Wand2 className="mr-2" size={18}/>} Gerar Avaliação
          </Button>
        </div>
      </form>
      {conteudo && <ResultPanel conteudo={conteudo} setConteudo={setConteudo}
        meta={{ disciplina: form.disciplina, serie: form.serie, tema: form.tema }}
        onRegenerate={() => submit()} tipo="avaliacao"/>}
    </ToolShell>
  );
}

/* Adaptar ---------------------------------------------------------------- */
export function AdaptarPage() {
  const [form, setForm] = useState({
    conteudo: "",
    acao: "Simplificar a linguagem",
    serie_destino: "",
    observacoes: "",
  });
  const [materiais, setMateriais] = useState([]);
  const [selecionadoId, setSelecionadoId] = useState("");
  const [resultado, setResultado] = useState("");
  const [loading, setLoading] = useState(false);
  const { refreshUsage } = useAuth();

  useEffect(() => {
    api.get("/materiais", { params: { limit: 50 }}).then(r => setMateriais(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selecionadoId) return;
    const m = materiais.find(x => x.id === selecionadoId);
    if (m) setForm(f => ({ ...f, conteudo: m.conteudo }));
  }, [selecionadoId, materiais]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.conteudo.trim()) { toast.error("Cole ou selecione um conteúdo."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/ai/adaptar", form);
      setResultado(data.conteudo); refreshUsage(); toast.success("Conteúdo adaptado!");
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  return (
    <ToolShell title="Adaptar Conteúdo" subtitle="Cole um conteúdo ou selecione um já criado — a IA adapta para você.">
      <form onSubmit={submit} className="grid gap-4 bg-white rounded-2xl border border-slate-200 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Ação">
            <SelectField value={form.acao} onChange={v => setForm({...form, acao: v})} options={ACOES_ADAPTAR} testid="select-acao"/>
          </Field>
          <Field label="Selecionar material salvo (opcional)">
            <Select value={selecionadoId} onValueChange={setSelecionadoId}>
              <SelectTrigger className="rounded-xl h-11" data-testid="select-material"><SelectValue placeholder="Escolha um material..."/></SelectTrigger>
              <SelectContent>{materiais.map(m => <SelectItem key={m.id} value={m.id}>{m.titulo} · {m.tipo}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Série de destino (se aplicável)">
          <SelectField value={form.serie_destino} onChange={v => setForm({...form, serie_destino: v})} options={SERIES} testid="select-serie-destino" placeholder="Manter a mesma"/>
        </Field>
        <Field label="Conteúdo original">
          <Textarea value={form.conteudo} onChange={e => setForm({...form, conteudo: e.target.value})} className="rounded-xl min-h-[240px] font-mono text-sm" placeholder="Cole aqui o conteúdo original..." data-testid="input-conteudo-original"/>
        </Field>
        <Field label="Observações">
          <Textarea value={form.observacoes} onChange={e => setForm({...form, observacoes: e.target.value})} className="rounded-xl" data-testid="input-observacoes"/>
        </Field>
        <div className="flex justify-end">
          <Button disabled={loading} className="rounded-full bg-[#1F3A8A] hover:bg-[#15265b] px-8 h-12 text-base font-semibold" data-testid="btn-generate">
            {loading ? <Loader2 className="animate-spin mr-2" size={18}/> : <Wand2 className="mr-2" size={18}/>} Adaptar Conteúdo
          </Button>
        </div>
      </form>
      {resultado && <ResultPanel conteudo={resultado} setConteudo={setResultado}
        meta={{ disciplina: "Adaptação", serie: form.serie_destino || "-", tema: form.acao }}
        onRegenerate={() => submit()} tipo="adaptacao"/>}
    </ToolShell>
  );
}
